import { BaseModel } from '@/modules/core/app/models/abstracts/BaseModel';
import { DataSourceType } from '@/modules/core/app/constants/datasource.constants';
import { DashboardFilterItem } from '@/modules/core/dashboard/models/DashboardFilterItem';
import { cloneDeep } from 'lodash';

export class DashboardFilters extends BaseModel {
  /**
   * @var {DashboardFilterItem}
   */
  client;

  /**
   * @var {DashboardFilterItem}
   */
  client_group;

  /**
   * @var {DashboardFilterItem}
   */
  client_category_id;

  /**
   * @var {DashboardFilterItem}
   */
  client_additional_details_1;

  /**
   * @var {DashboardFilterItem}
   */
  client_additional_details_2;

  /**
   * @var {DashboardFilterItem}
   */
  client_additional_details_3;

  /**
   * @var {DashboardFilterItem}
   */
  client_city;

  /**
   * @var {DashboardFilterItem}
   */
  client_state;

  /**
   * @var {DashboardFilterItem}
   */
  client_country;

  /**
   * @var {DashboardFilterItem}
   */
  client_naics_code;

  /**
   * @var {DashboardFilterItem}
   */
  cluster;

  /**
   * @var {DashboardFilterItem}
   */
  smart_campaign;

  /**
   * @var {Object}
   */
  widget_data_warehouse = {};

  /**
   * @var {Object}
   */
  widget_sourced = {};

  constructor(model = {}) {
    super();

    this.client = DashboardFilterItem.factory(model[DataSourceType.CLIENT]);
    this.client_group = DashboardFilterItem.factory(model[DataSourceType.CLIENT_GROUP]);
    this.client_category_id = DashboardFilterItem.factory(model[DataSourceType.CLIENT_CATEGORY]);
    this.client_country = DashboardFilterItem.factory(model[DataSourceType.CLIENT_COUNTRY]);
    this.client_state = DashboardFilterItem.factory(model[DataSourceType.CLIENT_STATE]);
    this.client_city = DashboardFilterItem.factory(model[DataSourceType.CLIENT_CITY]);
    this.client_naics_code = DashboardFilterItem.factory(model[DataSourceType.CLIENT_NAICS_CODE]);
    this.client_additional_details_1 = DashboardFilterItem.factory(
      model[DataSourceType.CLIENT_ADDITIONAL_DETAILS_1]
    );
    this.client_additional_details_2 = DashboardFilterItem.factory(
      model[DataSourceType.CLIENT_ADDITIONAL_DETAILS_2]
    );
    this.client_additional_details_3 = DashboardFilterItem.factory(
      model[DataSourceType.CLIENT_ADDITIONAL_DETAILS_3]
    );
    this.cluster = DashboardFilterItem.factory(model[DataSourceType.CLUSTER]);
    this.smart_campaign = DashboardFilterItem.factory(model[DataSourceType.SMART_CAMPAIGN]);
    this.widget_data_warehouse = this._convertJsonToDashboardFilterItem(
      model.widget_data_warehouse
    );
    this.widget_sourced = this._convertJsonToDashboardFilterItem(model.widget_sourced);
  }

  _convertJsonToDashboardFilterItem(json) {
    if (!json) {
      return null;
    }
    json = cloneDeep(json);
    Object.keys(json).forEach((dashboardId) => {
      if (json[dashboardId]) {
        Object.keys(json[dashboardId]).forEach((key) => {
          json[dashboardId][key] = DashboardFilterItem.factory(json[dashboardId][key]);
        });
      }
    });
    return json;
  }

  /**
   * @returns {boolean}
   */
  hasFilters() {
    return !!(
      this.client.values.length ||
      this.client_category_id.values.length ||
      this.client_group.values.length ||
      this.client_additional_details_1.values.length ||
      this.client_additional_details_2.values.length ||
      this.client_additional_details_3.values.length ||
      this.client_country.values.length ||
      this.client_state.values.length ||
      this.client_city.values.length ||
      this.client_naics_code.values.length ||
      this.cluster.values.length ||
      this.smart_campaign.values.length
    );
  }

  /**
   * @returns {boolean}
   */
  isClientFilter() {
    return !!this.client.values.length;
  }

  /**
   * @returns {boolean}
   */
  isClientGroupFilter() {
    return !!this.client_group.values.length;
  }

  /**
   * @returns {boolean}
   */
  isClusterFilter() {
    return !!this.cluster.values.length;
  }

  /**
   * @returns {boolean}
   */
  isSmartCampaignFilter() {
    return !!this.smart_campaign.values.length;
  }

  /**
   * @returns {*}
   */
  getActiveFilter() {
    let activeFilter = null;
    if (this.client.values.length) {
      activeFilter = this.client;
    } else if (this.client_category_id.values.length) {
      activeFilter = this.client_category_id;
    } else if (this.client_group.values.length) {
      activeFilter = this.client_group;
    } else if (this.client_additional_details_1.values.length) {
      activeFilter = this.client_additional_details_1;
    } else if (this.client_additional_details_2.values.length) {
      activeFilter = this.client_additional_details_2;
    } else if (this.client_additional_details_3.values.length) {
      activeFilter = this.client_additional_details_3;
    } else if (this.client_country.values.length) {
      activeFilter = this.client_country;
    } else if (this.client_state.values.length) {
      activeFilter = this.client_state;
    } else if (this.client_city.values.length) {
      activeFilter = this.client_city;
    } else if (this.client_naics_code.values.length) {
      activeFilter = this.client_naics_code;
    } else if (this.cluster.values.length) {
      activeFilter = this.cluster;
    } else if (this.smart_campaign.values.length) {
      activeFilter = this.smart_campaign;
    }
    return activeFilter ? activeFilter.values[0] : null;
  }

  getFilters() {
    const filters = {};
    filters[DataSourceType.CLIENT] = this.client;
    filters[DataSourceType.CLIENT_CATEGORY] = this.client_category_id;
    filters[DataSourceType.CLIENT_GROUP] = this.client_group;
    filters[DataSourceType.CLIENT_ADDITIONAL_DETAILS_1] = this.client_additional_details_1;
    filters[DataSourceType.CLIENT_ADDITIONAL_DETAILS_2] = this.client_additional_details_2;
    filters[DataSourceType.CLIENT_ADDITIONAL_DETAILS_3] = this.client_additional_details_3;
    filters[DataSourceType.CLIENT_COUNTRY] = this.client_country;
    filters[DataSourceType.CLIENT_STATE] = this.client_state;
    filters[DataSourceType.CLIENT_CITY] = this.client_city;
    filters[DataSourceType.CLIENT_NAICS_CODE] = this.client_naics_code;
    filters[DataSourceType.CLUSTER] = this.cluster;
    filters[DataSourceType.SMART_CAMPAIGN] = this.smart_campaign;
    return filters;
  }
}
